import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Palette, Ban } from 'lucide-react';
import { useConfiguratorStore } from '@store/useConfiguratorStore';
import './ColorPicker.css';

// Standard Colors
const presetColors = [
    '#FFFFFF', '#000000', '#1E3A8A', '#EC4899', '#FBBF24', '#C026D3', '#16A34A',
    '#2563EB', '#DB2777', '#F59E0B', '#A21CAF', '#15803D',
];

// Jar Specific Colors
const jarColors = [
    '#000000', // BLACK
    '#00008B', // DARK BLUE
    '#008000', // GREEN
    '#ADD8E6', // LIGHT BLUE
    '#FFA500', // ORANGE
    '#FFC0CB', // PINK
    '#800080', // PURPLE
    '#FF0000', // RED
    '#FFFFFF', // WHITE
    '#FFFF00', // YELLOW
];

interface ColorPickerProps {
    selectedPart?: string | null;
}

export default function ColorPicker({ selectedPart = null }: ColorPickerProps) {
    const { updateAllColors, updatePartColor, currentModel } = useConfiguratorStore();

    // Select specific palette based on model
    const bgColors = currentModel === 'jar' ? jarColors : presetColors;

    const [selectedColor, setSelectedColor] = useState(bgColors[0]);
    const colorInputRef = useRef<HTMLInputElement>(null);

    // Reset selection if model changes
    useEffect(() => {
        const colors = currentModel === 'jar' ? jarColors : presetColors;
        setSelectedColor(colors[0]);
    }, [currentModel]);

    const handleColorSelect = (color: string) => {
        setSelectedColor(color);

        // Apply color to selected part or all parts
        if (selectedPart === '__ALL__') {
            updateAllColors(color);
        } else if (selectedPart) {
            // Check if it's a Print Area (by checking store, or just trying)
            // We need to know if selectedPart ID matches a Print Area
            const isPrintArea = useConfiguratorStore.getState().printAreas.some(pa => pa.id === selectedPart);

            if (isPrintArea) {
                useConfiguratorStore.getState().updatePrintAreaColor(selectedPart, color);
            } else {
                updatePartColor(selectedPart, color);
            }
        } else {
            // Optional: User hasn't selected a part. 
            // We could default to 'All', but that causes the bug user reported.
            // For now, let's do nothing or show a toast if we had one.
            console.warn("No part selected for coloring");
        }
    };

    const handleResetColor = () => {
        // Reset local selection visualization
        setSelectedColor('');

        if (selectedPart === '__ALL__') {
            useConfiguratorStore.getState().clearColorCustomizations();
            // Also need to clear all print area backgrounds if we want a true global reset?
            // For now, keep it simple and just clear customization list.
        } else if (selectedPart) {
            const isPrintArea = useConfiguratorStore.getState().printAreas.some(pa => pa.id === selectedPart);
            if (isPrintArea) {
                useConfiguratorStore.getState().updatePrintAreaColor(selectedPart, null);
            } else {
                useConfiguratorStore.getState().removeColorCustomization(selectedPart);
            }
        }
    };

    const handleScrollLeft = () => {
        const container = document.querySelector('.color-swatches-scroll');
        if (container) {
            container.scrollBy({ left: -100, behavior: 'smooth' });
        }
    };

    const handleScrollRight = () => {
        const container = document.querySelector('.color-swatches-scroll');
        if (container) {
            container.scrollBy({ left: 100, behavior: 'smooth' });
        }
    };

    const handleCustomColorClick = () => {
        // Trigger the native color picker
        colorInputRef.current?.click();
    };

    const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newColor = e.target.value;
        handleColorSelect(newColor);
    };

    return (
        <div className="color-picker-horizontal">
            {/* Scrollable Row */}
            <div className="color-scroll-container">
                {/* Color Picker Button */}
                <div style={{ position: 'relative' }}>
                    <button
                        className="color-tool-btn"
                        title="Choose Custom Color"
                        onClick={handleCustomColorClick}
                    >
                        <Palette size={18} />
                    </button>

                    {/* HTML5 Color Input - positioned near button */}
                    <input
                        ref={colorInputRef}
                        type="color"
                        id="custom-color-picker"
                        aria-label="Choose Custom Color"
                        value={selectedColor}
                        onChange={handleCustomColorChange}
                        className="color-input-hidden"
                    />
                </div>

                {/* Left Arrow */}
                <button
                    className="color-arrow-btn"
                    onClick={handleScrollLeft}
                    title="Previous"
                >
                    <ChevronLeft size={18} />
                </button>

                {/* Color Swatches (Scrollable) */}
                <div className="color-swatches-scroll">
                    {/* Reset / No Color Option */}
                    <button
                        className="color-swatch-btn reset-btn"
                        style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onClick={handleResetColor}
                        title="Reset Color"
                    >
                        <Ban size={14} color="#ef4444" />
                    </button>

                    {bgColors.map((color, index) => (
                        <button
                            key={index}
                            className={`color-swatch-btn ${selectedColor === color ? 'active' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => handleColorSelect(color)}
                            title={color}
                        />
                    ))}
                </div>

                {/* Right Arrow */}
                <button
                    className="color-arrow-btn"
                    onClick={handleScrollRight}
                    title="Next"
                >
                    <ChevronRight size={18} />
                </button>
            </div>
        </div>
    );
}
